// Les gestes d'un parcours (D-141, maquettes f5a à f5d) : la fiche d'un simulateur (« Lancer »), le simulateur en plein
// écran DANS l'app (même origine, public/formation/), son score reçu par message et enregistré (le meilleur compte), le
// retour ; « S'entraîner librement » (D-145 : le simulateur complet, sans note) ; les fiches de la table ; la vidéo du
// référent (vue quand le lecteur arrive à la fin) ; un support du club ; le quiz (tiré et corrigé en base).
import {
  PARCOURS, SOUND_RULE, dotStates, freeUrl, needLabel, withPerson, notCountedWithoutSound, readSimMessage, shortDate, simHasVoice, simUrl, stepDef, stepOfSim, stepTitle,
  stepsLine, youtubeId,
  type ParcoursKey, type SimInfo, type SimKey, type StepState,
} from '../domain/formation';
import {
  formationMark, formationQuizDraw, formationQuizSubmit, formationSimScore, formationSupportFile,
  type FormationState, type QuizDraw, type SimResult, type Support,
} from '../lib/formation-api';
import type { Ctx } from './context';
import { $, $$, html, mount, openSheet, toast, type Html } from './dom';
import { bar, beta, dots, note, untilLabel } from './formation-parts';

/** Ce qu'un geste sait de la page d'où il part : le compte, l'état lu, le parcours, de quoi la redessiner. */
export interface Run { ctx: Ctx; st: FormationState; p: ParcoursKey; reload: () => Promise<void> }

const SIM_NAME: Record<SimInfo['sim'], string> = { table: 'Simulateur de table', gestes: 'Simulateur des gestes', placement: 'Simulateur du placement' };
const lower = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
/** « Simulateur de table · pas à pas ». */
export const simTitle = (s: SimInfo) => `${SIM_NAME[s.sim]} · ${lower(s.short)}`;
const club = (r: Run) => r.ctx.club!.id;
const stepsOf = (r: Run) => r.st.parcours[r.p]?.steps ?? [];
const indexOf = (r: Run, key: string) => stepsOf(r).findIndex(s => s.step === key) + 1;

// --- f5a : la fiche d'un simulateur -----------------------------------------------------------------------------------
export function openSimSheet(r: Run, key: string): void {
  const s = stepsOf(r).find(x => x.step === key);
  const sim = stepDef(r.p, key)?.sim;
  if (!s || !sim) return;
  const th = s.threshold ?? 0;
  const [n, ...unit] = sim.count.split(' ');
  const best = s.best != null ? html`<span>Ton meilleur : <b>${s.best} %</b> · ${s.tries ?? 0} ${(s.tries ?? 0) > 1 ? 'essais' : 'essai'}${s.last_at ? `, le ${shortDate(s.last_at)}` : ''}</span>`
    : html`<span>Pas encore lancé</span>`;
  openSheet(simTitle(sim), html`
    <div class="form">
      <button type="button" class="fo-frame" data-launch aria-label="Lancer le simulateur"><img src="${sim.thumb}" alt=""><span class="play"><b></b></span><span class="lbl">${sim.frame}</span></button>
      <p class="fo-s">${sim.about}</p>
      <div class="fo-facts"><div><b>${n}</b><small>${unit.join(' ')}</small></div><div><b>${sim.minutes} min</b><small>environ</small></div><div class="hit"><b>${th} %</b><small>pour réussir</small></div></div>
      <div class="fo-score">${best}<span class="muted small">${needLabel(s)}</span>${bar(s.best ?? 0, th, s.done)}</div>
      <p class="hint">${sim.hint}</p>
      ${simHasVoice(sim) ? html`<p class="hint"><strong>Avec la voix :</strong> touche « Activer le son » en ouvrant, ton téléphone le demande avant de parler.
        ${stepDef(r.p, key)?.sound ? `${SOUND_RULE} Son coupé, l’essai ne compte pas (sans voix française sur ton téléphone, le texte s’affiche et l’essai compte).` : 'Sans le son, les gestes de l’arbitre restent là.'}</p>` : ''}
      <button type="button" class="btn btn-primary" data-launch data-guide="fo-launch-sim">Lancer le simulateur</button>
      <p class="muted small fo-center">En plein écran · tu peux quitter à tout moment</p>
      <button type="button" class="btn btn-ghost" data-free>S’entraîner librement · sans note</button>
    </div>`, (sheet, close) => {
    for (const b of $$<HTMLButtonElement>(sheet, '[data-launch]')) b.addEventListener('click', () => { close(); openSimFull(r, key); });
    $(sheet, '[data-free]')?.addEventListener('click', () => { close(); openFree(sim.sim, r.ctx.uid); });
  });
}

// --- f5b, f5d : le simulateur en plein écran, dans l'app --------------------------------------------------------------
let openFull: (() => void) | null = null;
/** Un plein écran (simulateur, fiches) : la barre « ‹ Quitter », l'iframe de la même origine. */
function fullFrame(title: string, top: Html, src: string, onMessage: (data: unknown, quit: () => void, setTop: (h: Html) => void) => void, onQuit: () => void): void {
  const at = location.hash;
  openFull?.();
  const host = document.createElement('div');
  host.className = 'fo-full';
  host.setAttribute('role', 'dialog');
  host.setAttribute('aria-modal', 'true');
  host.setAttribute('aria-label', title);
  host.innerHTML = html`<div class="fo-full-top" data-guide="fo-full-top"><button type="button" class="fo-x" data-quit aria-label="Quitter">‹ Quitter</button>${top}</div>
    <iframe title="${title}" src="${src}" allow="autoplay"></iframe>`.s;
  document.body.append(host);
  const prev = document.documentElement.style.overflow;
  document.documentElement.style.overflow = 'hidden';
  const frame = host.querySelector<HTMLIFrameElement>('iframe')!;
  const setTop = (h: Html) => {
    const bar = $(host, '.fo-full-top')!;
    bar.querySelectorAll(':scope > :not([data-quit])').forEach(x => x.remove());
    bar.insertAdjacentHTML('beforeend', h.s);
    $(bar, '[data-quit-end]')?.addEventListener('click', () => quit());
  };
  const listen = (e: MessageEvent) => {
    // Seulement le simulateur de cette page : même origine, cette iframe.
    if (e.origin !== location.origin || e.source !== frame.contentWindow) return;
    onMessage(e.data, () => quit(), setTop);
  };
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') quit(); };
  const onHash = () => quit();
  /** Quitter : la page d'où l'on vient se redessine (le score compté), sauf si l'adresse a changé (retour du navigateur). */
  function quit(): void {
    window.removeEventListener('message', listen);
    window.removeEventListener('hashchange', onHash);
    document.removeEventListener('keydown', onKey);
    document.documentElement.style.overflow = prev;
    host.remove();
    openFull = null;
    if (location.hash === at) onQuit();
  }
  openFull = () => quit();
  window.addEventListener('message', listen);
  window.addEventListener('hashchange', onHash);
  document.addEventListener('keydown', onKey);
  $(host, '[data-quit]')!.addEventListener('click', () => quit());
}

export function openSimFull(r: Run, key: string): void {
  const s = stepsOf(r).find(x => x.step === key);
  const sim = stepDef(r.p, key)?.sim;
  if (!s || !sim) return;
  const total = stepsOf(r).length, i = indexOf(r, key);
  let last: { res: SimResult; msg: ReturnType<typeof readSimMessage> } | null = null;
  const head = (h: Html) => h;
  fullFrame(simTitle(sim), head(html`<div><strong>${sim.short} · étape ${i} sur ${total}</strong><small>${r.p === 'otm' ? PARCOURS[r.p].title : PARCOURS[r.p].name.replace(/ ★$/, '')} · ton meilleur : ${s.best != null ? `${s.best} %` : 'pas encore'}</small></div>${beta()}`),
    withPerson(simUrl(sim), r.ctx.uid), (data, _quit, setTop) => {
      const m = readSimMessage(data);
      if (!m || m.kind !== 'score' || stepOfSim(r.p, m.sim, m.mode)?.key !== key) return;
      // D-145 : « comme en match » joué sans le son (l'appareil a une voix française) : l'essai ne compte pas, il n'est pas envoyé.
      if (notCountedWithoutSound(r.p, key, m)) {
        setTop(html`<div><strong>${m.pct} % · sans le son : pas compté</strong><small>${SOUND_RULE}</small></div>`);
        return;
      }
      const detail: Record<string, unknown> = { ...(m.ok != null ? { ok: m.ok } : {}), ...(m.err != null ? { err: m.err } : {}), ...(m.miss != null ? { miss: m.miss } : {}),
        ...(m.score != null ? { score: m.score, total: m.total } : {}), ...(m.errs?.length ? { errs: m.errs.slice(0, 3) } : {}), ...(m.sound !== undefined ? { sound: m.sound } : {}) };
      void formationSimScore(club(r), r.p, key, m.pct, detail).then(res => {
        last = { res, msg: m };
        setTop(html`<div><strong>${m.pct} % · ${res.passed ? 'étape réussie ✓' : needLabel({ kind: 'sim', threshold: res.threshold })}</strong>
          <small>Ton meilleur : ${res.best} % · ${res.tries} ${res.tries > 1 ? 'essais' : 'essai'}</small></div>
          <button type="button" class="btn btn-primary btn-sm" data-quit-end>Voir le résultat</button>`);
      }).catch(() => toast('Score non enregistré : vérifie ta connexion.', 'ko'));
    }, () => {
      void r.reload().then(() => { if (last) openResultSheet(r, key, last.res, last.msg); });
    });
}

/** « S'entraîner librement » (D-145) : le simulateur complet, tous ses modes et sources ; le pont n'envoie aucun score (rien
 *  n'est noté, rien n'est écouté ici). */
export function openFree(sim: SimKey, uid?: string): void {
  fullFrame(`Entraînement libre · ${lower(SIM_NAME[sim])}`, html`<div><strong>Entraînement libre</strong><small>${SIM_NAME[sim]} · tous les modes, sans note</small></div>${beta()}`,
    withPerson(freeUrl(sim), uid), () => undefined, () => undefined);
}

// --- f5c : le retour, avec le score -------------------------------------------------------------------------------------
function openResultSheet(r: Run, key: string, res: SimResult, msg: ReturnType<typeof readSimMessage>): void {
  const sim = stepDef(r.p, key)?.sim;
  if (!sim) return;
  const prog = res.progress;
  const total = prog.total, done = prog.done;
  const m = msg && msg.kind === 'score' ? msg : null;
  const counts = m?.ok != null ? `${m.ok} saisies justes, ${(m.err ?? 0) + (m.miss ?? 0)} ${(m.err ?? 0) + (m.miss ?? 0) > 1 ? 'erreurs' : 'erreur'}`
    : m?.score != null ? `${m.score} sur ${m.total}` : '';
  const line = res.prev_best == null ? 'Ton premier essai' : res.best > res.prev_best ? `Ton meilleur passe de ${res.prev_best} à ${res.best} %` : `Ton meilleur reste ${res.best} %`;
  const next = prog.steps.find(x => !x.done && x.kind !== 'practice' && x.step !== key);
  const nextDef = next ? stepDef(r.p, next.step) : undefined;
  const errLine = m?.errs?.[0];
  openSheet(simTitle(sim), html`
    <div class="form">
      <div class="fo-res" data-guide="fo-result">${res.passed ? html`<span class="chip chip-ok">✓ Étape réussie</span>` : html`<span class="chip chip-mute">Pas encore</span>`}
        <span class="fo-res-n ${res.passed ? '' : 'ko'}">${res.pct} %</span>
        <p><strong>${line}</strong></p><p class="muted small">${needLabel({ kind: 'sim', threshold: res.threshold }, res.passed).replace(/^i/, 'I')}${counts ? ` · ${counts}` : ''}</p></div>
      <div class="gr-slot"><div style="display:grid;gap:6px;width:100%"><span class="eyebrow" style="margin:0">Ton ${PARCOURS[r.p].title.replace('Parcours', 'parcours')}</span><strong>${stepsLine(done, total)}</strong>
        ${dots(dotStates(prog))}</div></div>
      ${errLine ? note('target', html`<strong>À revoir :</strong> ${errLine}`) : ''}
      ${next ? html`<p class="eyebrow eyebrow-accent mo-eyebrow">La suite</p>
        <div class="gr-slot"><div><strong>${nextDef?.sim ? simTitle(nextDef.sim).replace('Simulateur de table · ', 'Simulateur · ') : stepTitle(r.p, next)}</strong>
          <span class="muted small">${nextDef?.sim ? `${nextDef.sim.count} · ${needLabel(next)}` : needLabel(next)}</span></div>
          ${next.kind === 'sim' || next.kind === 'quiz' ? html`<button type="button" class="btn btn-primary btn-sm" data-next="${next.step}">${next.kind === 'quiz' ? 'Commencer' : 'Lancer'}</button>` : ''}</div>` : ''}
      ${res.passed ? '' : html`<button type="button" class="btn btn-primary" data-again>Recommencer</button>`}
      <button type="button" class="btn btn-ghost" data-close>Revenir au parcours</button>
    </div>`, (sheet, close) => {
    $(sheet, '[data-again]')?.addEventListener('click', () => { close(); openSimFull(r, key); });
    $(sheet, '[data-next]')?.addEventListener('click', (e) => {
      const k = (e.currentTarget as HTMLElement).dataset.next!;
      close();
      if (stepsOf(r).find(x => x.step === k)?.kind === 'quiz') void openQuiz(r, 'quiz'); else openSimSheet(r, k);
    });
  });
}

// --- Les fiches de la table (Arceau) : en plein écran, « M'entraîner » ouvre l'étape du simulateur ------------------------
export function openFiches(r: Run, url: string): void {
  void formationMark(club(r), r.p, 'doc', 'open').catch(() => undefined);
  let marked = false;
  fullFrame('Les fiches de la table', html`<div><strong>Les fiches de la table</strong><small data-fiches-n>12 gestes · une fiche et un test chacun</small></div>${beta()}`,
    withPerson(url, r.ctx.uid), (data, quit) => {
      const m = readSimMessage(data);
      if (!m) return;
      if (m.kind === 'fiches') {
        const n = document.querySelector<HTMLElement>('[data-fiches-n]');
        if (n) n.textContent = `${m.passed} ${m.passed > 1 ? 'tests réussis' : 'test réussi'} sur ${m.total}`;
        if (m.total > 0 && m.passed >= m.total && !marked) {
          marked = true;
          void formationMark(club(r), r.p, 'doc', 'done', 'fiches').then(() => toast('Les 12 tests sont réussis : l’étape est faite')).catch(() => undefined);
        }
      } else if (m.kind === 'open-sim') {
        const step = stepOfSim(r.p, 'table', m.mode);
        quit();
        if (step) window.setTimeout(() => openSimSheet(r, step.key), 350);
      }
    }, () => void r.reload());
}

// --- Un support du club : un lien (ouvert à côté), le PDF du club, ou la vidéo YouTube ---------------------------------------
export async function openSupport(r: Run, s: Support): Promise<void> {
  const step = `sup:${s.id}`;
  if (s.kind === 'video' && youtubeId(s.url)) { openVideo(r, s); return; }
  if (s.url) {
    window.open(s.url, '_blank', 'noopener');
    await formationMark(club(r), r.p, step, 'open').catch(() => undefined);
    await r.reload();
    return;
  }
  // Le PDF du club : la fenêtre s'ouvre tout de suite (Safari), le fichier arrive ensuite.
  const w = window.open('', '_blank');
  try {
    const f = await formationSupportFile(s.id);
    if (!f) throw new Error('absent');
    const bytes = Uint8Array.from(atob(f.data), c => c.charCodeAt(0));
    const blobUrl = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    if (w) w.location.href = blobUrl; else window.open(blobUrl, '_blank');
    await formationMark(club(r), r.p, step, 'open').catch(() => undefined);
    await r.reload();
  } catch { w?.close(); toast('Document indisponible pour le moment.', 'ko'); }
}

/** La vidéo choisie par le référent : vue quand le lecteur arrive à la fin ; si le lecteur ne le dit pas, « Je l'ai vue
 *  en entier » (déclaratif). */
export function openVideo(r: Run, s: Support): void {
  const id = youtubeId(s.url);
  if (!id) return;
  const step = `sup:${s.id}`;
  void formationMark(club(r), r.p, step, 'open').catch(() => undefined);
  const origin = encodeURIComponent(location.origin);
  let done = false, heard = false;
  const finish = (how: 'vue' | 'declare') => {
    if (done) return;
    done = true;
    void formationMark(club(r), r.p, step, 'done', how).then(() => { toast(how === 'vue' ? 'Vidéo vue : l’étape est faite' : 'C’est noté : l’étape est faite'); void r.reload(); })
      .catch(() => { done = false; toast('Enregistrement impossible.', 'ko'); });
  };
  const listen = (e: MessageEvent) => {
    if (!/^https:\/\/www\.youtube(-nocookie)?\.com$/.test(e.origin)) return;
    let d: { event?: string; info?: unknown } | null = null;
    try { d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data; } catch { return; }
    if (!d || typeof d !== 'object') return;
    heard = true;
    const state = d.event === 'onStateChange' ? d.info : d.event === 'infoDelivery' && d.info && typeof d.info === 'object' ? (d.info as { playerState?: number }).playerState : undefined;
    if (state === 0) finish('vue');
  };
  window.addEventListener('message', listen);
  openSheet(s.title, html`
    <div class="form">
      <div class="fo-yt"><iframe title="${s.title}" src="https://www.youtube-nocookie.com/embed/${id}?enablejsapi=1&rel=0&playsinline=1&origin=${origin}"
        allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen referrerpolicy="strict-origin-when-cross-origin"></iframe></div>
      ${s.note ? note('quote', html`« ${s.note} » ${s.by ?? ''}`) : ''}
      <p class="hint">L’étape se coche quand la vidéo arrive à la fin. La vidéo reste sur YouTube : Arceau ne la copie pas.</p>
      <button type="button" class="btn btn-ghost" id="fo-vu" hidden>Je l’ai vue en entier</button>
    </div>`, (sheet) => {
    const frame = $(sheet, 'iframe') as HTMLIFrameElement;
    frame.addEventListener('load', () => {
      // Le lecteur YouTube ne parle que si on l'écoute ; sans réponse, « Je l'ai vue en entier » prend le relais.
      const say = (o: object) => frame.contentWindow?.postMessage(JSON.stringify({ ...o, id: 'fo', channel: 'widget' }), '*');
      say({ event: 'listening' });
      say({ event: 'command', func: 'addEventListener', args: ['onStateChange'] });
      window.setTimeout(() => { if (!heard) $(sheet, '#fo-vu')!.hidden = false; }, 5000);
    });
    $(sheet, '#fo-vu')!.addEventListener('click', () => finish('declare'));
  }, () => window.removeEventListener('message', listen));
}

// --- Le quiz : tiré en base (10 questions, la page citée), corrigé en base ---------------------------------------------------
export async function openQuiz(r: Run, kind: 'quiz' | 'renewal'): Promise<void> {
  let q: QuizDraw;
  try { q = await formationQuizDraw(club(r), r.p, kind); }
  catch (e) { toast(/juin/.test(String((e as Error).message)) ? 'La remise à niveau ouvre le 1er juin.' : 'Quiz indisponible pour le moment.', 'ko'); return; }
  const answers: (number | null)[] = q.questions.map(() => null);
  let i = 0;
  const title = kind === 'quiz' ? 'Le quiz de l’e-Marque' : `Remise à niveau · ${PARCOURS[r.p].name}`;
  openSheet(title, html`<div class="form" id="fo-quiz"></div>`, (sheet, close) => {
    const body = $(sheet, '#fo-quiz')!;
    const draw = () => {
      const x = q.questions[i]!;
      mount(body, html`
        <span class="fo-kind">Question ${i + 1} sur ${q.questions.length}${x.page ? html`<span class="fo-when">${x.club ? 'du club · ' : ''}${x.page}</span>` : x.club ? html`<span class="fo-when">du club</span>` : ''}</span>
        <strong class="fo-t">${x.q}</strong>
        <div class="fo-choices" data-guide="fo-quiz-choices">${x.choices.map((c, k) => html`
          <button type="button" class="fo-choice" data-k="${k}" aria-pressed="${String(answers[i] === k)}"><span class="fo-radio" aria-hidden="true"></span><span><strong>${c}</strong></span><span></span></button>`)}</div>
        <div class="fo-acts">${i > 0 ? html`<button type="button" class="btn btn-ghost" data-prev>‹ Précédente</button>` : html`<span></span>`}
          <button type="button" class="btn btn-primary" data-next ${answers[i] == null ? 'disabled' : ''}>${i + 1 < q.questions.length ? 'Suivante' : 'Rendre le quiz'}</button></div>
        <p class="hint">${kind === 'quiz' ? `${q.pass} bonnes réponses sur ${q.questions.length} pour réussir.` : `${q.pass} bonnes réponses sur ${q.questions.length} renouvellent ton badge.`} La correction vient à la fin, avec la page du document.</p>`);
      for (const b of $$<HTMLButtonElement>(body, '[data-k]')) b.addEventListener('click', () => { answers[i] = Number(b.dataset.k); draw(); });
      $(body, '[data-prev]')?.addEventListener('click', () => { i--; draw(); });
      $(body, '[data-next]')!.addEventListener('click', async (e) => {
        if (i + 1 < q.questions.length) { i++; draw(); return; }
        const btn = e.currentTarget as HTMLButtonElement;
        btn.disabled = true;
        try {
          const res = await formationQuizSubmit(q.run, answers);
          const wrong = res.corrections.map((c, k) => ({ c, x: q.questions[k]! })).filter(w => !w.c.ok);
          mount(body, html`
            <div class="fo-res">${res.passed ? html`<span class="chip chip-ok">✓ ${kind === 'quiz' ? 'Quiz réussi' : 'Badge renouvelé'}</span>` : html`<span class="chip chip-mute">Pas encore</span>`}
              <span class="fo-res-n ${res.passed ? '' : 'ko'}">${res.score}/${res.total}</span>
              <p class="muted small">Il ${res.passed ? 'fallait' : 'faut'} ${res.pass} bonnes réponses${res.until ? ` · ${untilLabel(res.until)}` : ''}</p></div>
            ${wrong.length ? html`<p class="fo-sec-h">À revoir</p><div>${wrong.map(w => html`<div class="fo-q-item"><strong>${w.x.q}</strong>
              <span class="muted">Ta réponse : ${w.c.given != null ? w.x.choices[w.c.given] ?? '·' : 'aucune'}</span>
              <span class="good">La bonne : ${w.x.choices[w.c.answer]}</span>${w.c.page ? html`<span class="muted small">Manuel, ${w.c.page}</span>` : ''}</div>`)}</div>` : html`<p class="fo-center">Aucune erreur.</p>`}
            ${res.passed ? '' : html`<button type="button" class="btn btn-primary" data-again>Recommencer</button>`}
            <button type="button" class="btn btn-ghost" data-done>Revenir au parcours</button>`);
          $(body, '[data-again]')?.addEventListener('click', () => { close(); void openQuiz(r, kind); });
          $(body, '[data-done]')!.addEventListener('click', close);
          void r.reload();
        } catch { btn.disabled = false; toast('Quiz non rendu : vérifie ta connexion.', 'ko'); }
      });
    };
    draw();
  });
}

export type { StepState };
